import { defined, definedMap, panic } from "@glideapps/ts-necessities";
import classNames from "classnames";
import { massageImageUrl } from "common/components/portable-renderers";
import { asMaybeDate, asMaybeString } from "common/computation-model/data";
import { ArrayScreenFormat } from "common/description";
import {
    anyDefined,
    convertDateFromTimeZoneAgnostic,
    convertDateToTimeZoneAgnostic,
    parseNumber,
} from "common/Support";
import type {
    WireBreadcrumbsComponent,
    WireChoiceComponent,
    WireDataGridListComponent,
    WireDateTimeFieldComponent,
    WireEmailFieldComponent,
    WireFilePickerComponent,
    WireImagePickerComponent,
    WireNumberFieldComponent,
    WirePhoneFieldComponent,
    WireSignInComponent,
    WireSwitchComponent,
    WireTextFieldComponent,
} from "fluent-components/base-components";
import type {
    WireBigNumbersComponent,
    WireButtonComponent,
    WireButtonsComponent,
    WireContactFormComponent,
    WireCustomComponent,
    WireFieldsComponent,
    WireHintComponent,
    WireImageComponent,
    WireListCardCollection,
    WireRichTextComponent,
    WireSeparatorComponent,
    WireTitleComponent,
    WireVideoComponent,
} from "fluent-components/fluent-components";
import type {
    WireContainerComponent,
    WireFormContainerComponent,
} from "fluent-components/fluent-components-containers";
import type { WireListComponentGeneric } from "fluent-components/fluent-components-spec";
import type { WireComponent, WireScreen } from "generator/wire";
import { useWireAppTheme } from "hooks/use-wireapp-theme";
import type { WireBackend } from "hydrated-ui/types";
import * as React from "react";
import { TailwindThemeProvider } from "sharedUI/lib/tailwind-theme-provider";
import { mergeTheme } from "sharedUI/lib/theme-utils";
import { css } from "styled-components";
import tw from "twin.macro";
import { AuthControllerPropsContextHack } from "webapp/auth-controller-props-context-hack";
import AuthController from "webapp/chrome/smart/auth-controller/auth-controller";
import {
    defaultWireAppAccentContextOverlay,
    defaultWireAppAccentContextOverlayUndo,
    defaultWireAppImageBrightenContextOverlay,
    defaultWireAppImageDarkenContextOverlay,
} from "webapp/lib/theme/core-themes/wireapp-theme";
import { styled } from "webapp/styled-components";
import { CardCollectionRenderer } from "webapp/ui/smart/card-collection/card-collection";
import type { PagesLoginSource } from "webapp/ui/smart/custom-sign-in/custom-wire-sign-in/wire-email-input";
import Img from "webapp/ui/smart/img/img";
import { Breadcrumbs } from "webapp/ui/views/breadcrumbs/breadcrumbs";
import { DataGrid } from "webapp/ui/views/data-grid/data-grid";
import { Fields } from "webapp/ui/views/fields/fields";
import { FormSection } from "webapp/ui/views/form-section/form-section";
import { WireBigNumbers } from "webapp/ui/views/wire-big-numbers/wire-big-numbers";
import { WireButtonBar } from "webapp/ui/views/wire-button-bar/wire-button-bar";
import { WireButton } from "webapp/ui/views/wire-button/wire-button";
import { WireChoice } from "webapp/ui/views/wire-choice/wire-choice";
import { SectionStyleProvider, WireContainer } from "webapp/ui/views/wire-container/wire-container";
import { WireCustom } from "webapp/ui/views/wire-custom/wire-custom";
import WireDateTimePicker from "webapp/ui/views/wire-date-time-picker/wire-date-time-picker";
import { WireField } from "webapp/ui/views/wire-field/wire-field";
import { WireFilePicker, WireImagePicker } from "webapp/ui/views/wire-file-picker/wire-file-picker";
import { WireHero } from "webapp/ui/views/wire-hero/wire-hero";
import { WireHint } from "webapp/ui/views/wire-hint/wire-hint";
import { WireImage } from "webapp/ui/views/wire-image/wire-image";
import { WireRichText } from "webapp/ui/views/wire-rich-text/wire-rich-text";
import { Separator } from "webapp/ui/views/wire-separator/wire-separator";
import { WireSwitch } from "webapp/ui/views/wire-switch/wire-switch";
import { WireVideo } from "webapp/ui/views/wire-video/wire-video";
import { WireComponentKind } from "wire/component-kind";
import { ButtonBarSize, HeroImageEffect, UIAlignment, UIBackgroundStyle } from "wire/types";
import { makeSingleActionSpreadProps } from "./wire-lib";
import type { WireRenderer } from "./wire-renderer";

interface Props {
    readonly currentScreen: WireScreen;
    readonly backend: WireBackend;
}

interface WireComponentRendererProps {
    readonly component: WireComponent;
    readonly backend: WireBackend;
}

const WireComponentRenderer: React.VFC<WireComponentRendererProps> = p => {
    const { component: untypedComponent, backend } = p;
    const { appKind } = backend;

    if (untypedComponent.kind === WireComponentKind.Button) {
        const component = untypedComponent as WireButtonComponent;
        if (component.action === undefined) return null;
        return (
            <WireContainer id={component.id}>
                <WireButtonBar
                    overrideAppearance={component.appearance}
                    alignment={UIAlignment.Center}
                    size={ButtonBarSize.Wide}
                    // title={component.description}
                    buttons={[component]}
                    backend={backend}
                />
            </WireContainer>
        );
    } else if (untypedComponent.kind === WireComponentKind.TextField) {
        const component = untypedComponent as WireTextFieldComponent;
        return (
            <WireContainer id={component.id}>
                <WireField
                    placeholder={component.placeholder}
                    dataType="text"
                    isEnabled={component.value.onChangeToken !== undefined}
                    error={component.value.error}
                    value={component.value.value}
                    size={component.size}
                    maximumChars={component.maxChars ?? undefined}
                    minimumChars={component.minChars ?? undefined}
                    onChange={newVal => backend.valueChanged(defined(component.value.onChangeToken), newVal)}
                    isRequired={component.isRequired}
                    title={component.title}
                />
            </WireContainer>
        );
    } else if (untypedComponent.kind === WireComponentKind.NumberField) {
        const component = untypedComponent as WireNumberFieldComponent;
        return (
            <WireContainer id={component.id}>
                <WireField
                    placeholder={component.placeholder}
                    dataType="number"
                    isEnabled={component.value.onChangeToken !== undefined}
                    error={component.value.error}
                    value={asMaybeString(component.value.value) ?? ""}
                    onChange={newVal =>
                        backend.valueChanged(defined(component.value.onChangeToken), parseNumber(newVal) ?? newVal)
                    }
                    isRequired={component.isRequired}
                    maximumChars={undefined}
                    title={component.title}
                />
            </WireContainer>
        );
    } else if (untypedComponent.kind === WireComponentKind.PhoneField) {
        const component = untypedComponent as WirePhoneFieldComponent;
        return (
            <WireContainer id={component.id}>
                <WireField
                    placeholder={component.placeholder}
                    dataType="phone"
                    isEnabled={component.value.onChangeToken !== undefined}
                    error={component.value.error}
                    value={asMaybeString(component.value.value) ?? ""}
                    onChange={newVal => backend.valueChanged(defined(component.value.onChangeToken), newVal)}
                    isRequired={component.isRequired}
                    maximumChars={undefined}
                    title={component.title}
                />
            </WireContainer>
        );
    } else if (untypedComponent.kind === WireComponentKind.EmailField) {
        const component = untypedComponent as WireEmailFieldComponent;
        return (
            <WireContainer id={component.id}>
                <WireField
                    placeholder={component.placeholder}
                    dataType="email"
                    isEnabled={component.value.onChangeToken !== undefined}
                    error={component.value.error}
                    value={asMaybeString(component.value.value) ?? ""}
                    onChange={newVal => backend.valueChanged(defined(component.value.onChangeToken), newVal)}
                    isRequired={component.isRequired}
                    maximumChars={undefined}
                    title={component.title}
                />
            </WireContainer>
        );
    } else if (untypedComponent.kind === WireComponentKind.DateTimeField) {
        const component = untypedComponent as WireDateTimeFieldComponent;
        return (
            <WireContainer id={component.id}>
                <WireDateTimePicker
                    variant={component.withTime ? "date-time" : "date"}
                    title={component.title}
                    placeholder={component.placeholder}
                    value={definedMap(asMaybeDate(component.value.value), convertDateFromTimeZoneAgnostic)}
                    pastAndOrFuture={component.pastOrFuture}
                    isEnabled={component.value.onChangeToken !== undefined}
                    onChange={newVal => {
                        if (newVal instanceof Date) {
                            newVal = convertDateToTimeZoneAgnostic(newVal);
                        }
                        backend.valueChanged(defined(component.value.onChangeToken), newVal);
                    }}
                    isRequired={component.isRequired}
                    appKind={appKind}
                />
            </WireContainer>
        );
    } else if (untypedComponent.kind === WireComponentKind.ImagePicker) {
        const component = untypedComponent as WireImagePickerComponent;
        return (
            <WireContainer id={component.id}>
                <WireImagePicker {...component} backend={backend} />
            </WireContainer>
        );
    } else if (untypedComponent.kind === WireComponentKind.FilePicker) {
        const component = untypedComponent as WireFilePickerComponent;
        return (
            <WireContainer id={component.id}>
                <WireFilePicker {...component} backend={backend} />
            </WireContainer>
        );
    } else if (untypedComponent.kind === WireComponentKind.Switch) {
        const component = untypedComponent as WireSwitchComponent;
        return (
            <WireContainer id={component.id}>
                <WireSwitch {...component} backend={backend} />
            </WireContainer>
        );
    } else if (untypedComponent.kind === WireComponentKind.Hero) {
        const component = untypedComponent as WireTitleComponent;
        return (
            <WireContainer id={component.id}>
                <WireHero {...component} backend={backend} />
            </WireContainer>
        );
    } else if (untypedComponent.kind === WireComponentKind.ContactForm) {
        const component = untypedComponent as WireContactFormComponent;
        return (
            <WireContainer id={component.id}>
                <FormSection
                    title={component.title ?? undefined}
                    description={component.description ?? undefined}
                    email={component.email?.value}
                    emailError={component.email?.error}
                    message={component.message?.value}
                    messageError={component.message?.error}
                    name={component.name?.value}
                    nameError={component.name?.error}
                    phone={component.phone?.value}
                    phoneError={component.phone?.error}
                    onEmailChange={definedMap(
                        component.email?.onChangeToken,
                        token => x => backend.valueChanged(token, x)
                    )}
                    onMessageChange={definedMap(
                        component.message?.onChangeToken,
                        token => x => backend.valueChanged(token, x)
                    )}
                    onNameChange={definedMap(
                        component.name?.onChangeToken,
                        token => x => backend.valueChanged(token, x)
                    )}
                    onPhoneChange={definedMap(
                        component.phone?.onChangeToken,
                        token => x => backend.valueChanged(token, x)
                    )}
                    onSubmit={definedMap(component.submitAction?.token, token => () => backend.runAction(token, false))}
                />
            </WireContainer>
        );
    } else if (untypedComponent.kind === WireComponentKind.DynamicButtonBar) {
        const component = untypedComponent as WireButtonsComponent;
        if (!component.buttons.some(a => a.action !== null && a.action !== undefined)) return null;
        return (
            <WireContainer id={component.id}>
                <WireButtonBar {...component} backend={backend} />
            </WireContainer>
        );
    } else if (untypedComponent.kind === WireComponentKind.Breadcrumbs) {
        const component = untypedComponent as WireBreadcrumbsComponent;
        return (
            <WireContainer id={component.id}>
                <Breadcrumbs {...component} backend={backend} />
            </WireContainer>
        );
    } else if (untypedComponent.kind === WireComponentKind.Container) {
        const component = untypedComponent as WireContainerComponent;
        // const { bgStyle, withBlur, backgroundImage, backgroundEffect } = component;
        return <WireComponentStack {...component} backend={backend} />;
    } else if (untypedComponent.kind === WireComponentKind.FormContainer) {
        const component = untypedComponent as WireFormContainerComponent;
        return (
            <WireComponentStack
                {...component}
                backgroundEffect={HeroImageEffect.None}
                withBlur={false}
                backend={backend}
            />
        );
    } else if (untypedComponent.kind === WireComponentKind.Fields) {
        const component = untypedComponent as WireFieldsComponent;
        return (
            <WireContainer id={component.id}>
                <Fields {...component} backend={backend} />
            </WireContainer>
        );
    } else if (untypedComponent.kind === WireComponentKind.Hint) {
        const component = untypedComponent as WireHintComponent;
        if (!anyDefined(component.action, component.actionTitle, component.description)) return null;
        return (
            <WireContainer id={component.id}>
                <WireHint {...component} backend={backend} />
            </WireContainer>
        );
    } else if (untypedComponent.kind === WireComponentKind.Separator) {
        const component = untypedComponent as WireSeparatorComponent;
        return (
            <WireContainer id={component.id}>
                <Separator {...component} backend={backend} />
            </WireContainer>
        );
    } else if (untypedComponent.kind === WireComponentKind.RichText) {
        const component = untypedComponent as WireRichTextComponent;
        return (
            <WireContainer id={component.id}>
                <WireRichText {...component} backend={backend} />
            </WireContainer>
        );
    } else if (untypedComponent.kind === WireComponentKind.Image) {
        const component = untypedComponent as WireImageComponent;
        return (
            <WireContainer id={component.id}>
                <WireImage {...component} backend={backend} />
            </WireContainer>
        );
    } else if (untypedComponent.kind === WireComponentKind.Video) {
        const component = untypedComponent as WireVideoComponent;
        return (
            <WireContainer id={component.id}>
                <WireVideo {...component} backend={backend} />
            </WireContainer>
        );
    } else if (untypedComponent.kind === WireComponentKind.SignIn) {
        const component = untypedComponent as WireSignInComponent;
        return <WireAuthController {...component} backend={backend} />;
    } else if (untypedComponent.kind === WireComponentKind.Custom) {
        const component = untypedComponent as WireCustomComponent;
        return <WireCustom {...component} backend={backend} />;
    } else if (untypedComponent.kind === WireComponentKind.BigNumbers) {
        const component = untypedComponent as WireBigNumbersComponent;
        return (
            <WireContainer id={component.id}>
                <WireBigNumbers {...component} backend={backend} />
            </WireContainer>
        );
    } else if (untypedComponent.kind === WireComponentKind.List) {
        const listComponent = untypedComponent as WireListComponentGeneric<ArrayScreenFormat>;
        if (listComponent.format === ArrayScreenFormat.CardCollection) {
            const component = untypedComponent as WireListCardCollection;
            return (
                <WireContainer id={component.id}>
                    <CardCollectionRenderer {...component} backend={backend} />
                </WireContainer>
            );
        } else if (listComponent.format === ArrayScreenFormat.Choice) {
            const component = untypedComponent as WireChoiceComponent;
            return (
                <WireContainer id={component.id}>
                    <WireChoice {...component} backend={backend} />
                </WireContainer>
            );
        } else if (listComponent.format === ArrayScreenFormat.DataGrid) {
            const component = untypedComponent as WireDataGridListComponent;
            return (
                <WireContainer id={component.id}>
                    <DataGrid
                        {...component}
                        onPageChange={v =>
                            component.paging?.pageIndex.onChangeToken &&
                            backend.valueChanged(component.paging.pageIndex.onChangeToken, v)
                        }
                    />
                </WireContainer>
            );
        } else {
            return panic(`Unknown list format ${listComponent.format}`);
        }
    } else {
        return panic(`Unknown component ${untypedComponent.kind}`);
    }
};

const WireAuthController: WireRenderer<WireSignInComponent> = p => {
    const { backend, onFailure, onSuccess, isSignUp } = p;

    const [pagesSource, setPagesSource] = React.useState<PagesLoginSource>(
        isSignUp ? "modal-sign-up" : "modal-sign-in"
    );

    const authProps = React.useContext(AuthControllerPropsContextHack);

    const onLoggedIn = React.useCallback(async () => {
        backend.runAction(onSuccess.token, false);
    }, [backend, onSuccess.token]);

    const onFailed = React.useCallback(() => {
        backend.runAction(onFailure.token, false);
    }, [backend, onFailure.token]);

    if (authProps === undefined) return null;

    return (
        <AuthController
            {...authProps}
            pagesSource={pagesSource}
            onPageSourceChanged={setPagesSource}
            onLoggedIn={onLoggedIn}
            onFailed={onFailed}
        />
    );
};

const ContainerSectionStyle = styled.section`
    &.style-dark,
    &.style-image.darken {
        background-color: ${p => p.theme.n700};
    }

    &.style-image {
        background-color: ${p => p.theme.n200};
    }

    &.style-highlight,
    &.style-image.lighten {
        background-color: ${p => p.theme.bgContainerHighlight};
    }

    &.style-highlight {
        box-shadow: 0 1px 0 ${p => p.theme.borderBase};
    }

    &.darken .background-image,
    &.lighten .background-image {
        opacity: 0.5;
    }

    &.blur {
        overflow: hidden;
    }

    &:first-child.full-bleed,
    &:first-child.style-none,
    .full-bleed + &.full-bleed,
    .style-none + &.style-none,
    .full-bleed + &.style-none {
        margin-top: 0;
    }
`;

// TODO: This doesn't belong in here, but there's a dependency cycle between
// this and `WireComponentRenderer`
export const WireComponentStack: WireRenderer<
    WireContainerComponent,
    { id?: string; specialButtons?: readonly (WireButtonComponent | null)[] }
> = React.memo(props => {
    const {
        containerStyle: background,
        id,
        backend,
        backgroundEffect = HeroImageEffect.None,
        backgroundImage,
        withBlur,
    } = props;
    let specialButtons: React.ReactNode = undefined;
    if (props.specialButtons !== undefined && props.specialButtons.length > 0) {
        specialButtons = (
            <WireContainer>
                <div tw="flex">
                    {props.specialButtons.map((c, i) => {
                        if (c === null) return null;
                        return (
                            <WireButton
                                appearance={c.appearance}
                                {...makeSingleActionSpreadProps(c.action, backend)}
                                key={i}
                                tw="mr-4">
                                {c.title ?? "Err"}
                            </WireButton>
                        );
                    })}
                </div>
            </WireContainer>
        );
    }
    let contents: React.ReactNode = (
        <div>
            {props.components.map((c, i) => {
                if (c === null) return null;
                return <WireComponentRenderer key={i} component={c} backend={props.backend} />;
            })}
            {specialButtons}
            {background === UIBackgroundStyle.None && <div />}
        </div>
    );
    let theme = useWireAppTheme();
    if (background === UIBackgroundStyle.Card) {
        theme = mergeTheme(theme, [defaultWireAppAccentContextOverlayUndo]);
        contents = (
            <div
                tw="z-10 w-full relative flex flex-col px-5 self-center
                    gp-sm:(px-6)
                    gp-md:(px-7)
                    gp-lg:(px-8)
                    gp-2xl:(max-width[1260px])"
                css={css`
                    .max-size-lg & {
                        max-width: 768px;
                    }

                    .max-size-xl & {
                        max-width: 1024px;
                    }
                `}>
                <div
                    tw="bg-bg-front shadow-md rounded-lg pb-6"
                    css={css`
                        .max-size-lg & {
                            max-width: 704px;
                        }

                        .max-size-xl & {
                            max-width: 960px;
                        }
                    `}>
                    <TailwindThemeProvider theme={theme}>{contents}</TailwindThemeProvider>
                </div>
            </div>
        );
    } else if (background === UIBackgroundStyle.Image && backgroundEffect === HeroImageEffect.Lighten) {
        theme = mergeTheme(theme, [defaultWireAppImageBrightenContextOverlay]);
        contents = <TailwindThemeProvider theme={theme}>{contents}</TailwindThemeProvider>;
    } else if (background === UIBackgroundStyle.Image) {
        theme = mergeTheme(theme, [defaultWireAppImageDarkenContextOverlay]);
        contents = <TailwindThemeProvider theme={theme}>{contents}</TailwindThemeProvider>;
    } else if (background === UIBackgroundStyle.Accent || background === UIBackgroundStyle.Dark) {
        theme = mergeTheme(theme, [defaultWireAppAccentContextOverlay]);
        contents = <TailwindThemeProvider theme={theme}>{contents}</TailwindThemeProvider>;
    } else if (background === UIBackgroundStyle.Highlight) {
        theme = mergeTheme(theme, [defaultWireAppAccentContextOverlayUndo]);
        contents = <TailwindThemeProvider theme={theme}>{contents}</TailwindThemeProvider>;
    }

    return (
        <SectionStyleProvider value={background}>
            <ContainerSectionStyle
                id={id}
                className={classNames(
                    "component-stack",
                    background !== UIBackgroundStyle.None && background !== UIBackgroundStyle.Card && "full-bleed",
                    background === UIBackgroundStyle.Accent && "accent-bg",
                    background === UIBackgroundStyle.Image && blur && "blur",
                    backgroundEffect.toLocaleLowerCase(),
                    `style-${background.toLowerCase()}`
                )}
                tw="isolate relative w-full flex justify-center mt-6 transition-opacity pb-6">
                {background === UIBackgroundStyle.Image && backgroundImage !== undefined && (
                    <Img
                        className={classNames("background-image", { blur: withBlur })}
                        tw="absolute inset-0 object-cover w-full h-full pointer-events-none filter"
                        css={css`
                            &.blur {
                                ${tw`transform scale-125 blur-md`}
                            }
                        `}
                        data-test="hero-background-image"
                        src={massageImageUrl(
                            backgroundImage ?? undefined,
                            {
                                width: window.innerWidth,
                                thumbnail: false,
                            },
                            undefined
                        )}
                        width={"100%"}
                        height={400}
                        draggable={false}
                    />
                )}
                <div className="section-container" tw="w-full relative flex flex-col">
                    {contents}
                </div>
            </ContainerSectionStyle>
        </SectionStyleProvider>
    );
});

export const WireApp: React.VFC<Props> = p => {
    const { currentScreen, backend } = p;

    const content = React.useMemo(() => {
        const items = currentScreen?.components ?? [];
        return items.map((i, index) => {
            if (i === null) return undefined;
            return <WireComponentRenderer key={index} component={i} backend={backend} />;
        });
    }, [backend, currentScreen?.components]);

    return <>{content}</>;
};
